import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { clamp, wrapAngle, damp } from '../../engine/math/easing';
import { Rng } from '../../engine/math/random';
import { sphere, box, cone, combine } from '../../engine/geo/primitives';
import { translate, setEmissive, rotateX } from '../../engine/geo/mesh';
import type { Ctx, Renderable } from '../Ctx';
import type { Player } from '../player/Player';
import type { Items, ItemState } from '../items/Items';
import type { Particles } from '../fx/Particles';
import { BIOMES, BiomeId, biomeAt } from '../world/Layout';
import { SpringF } from '../../engine/math/spring';

type MagpieState = 'roam' | 'swoop' | 'carry' | 'flee';
const SURFACE: BiomeId[] = ['hearth', 'marsh', 'dunes', 'ironwood'];

/**
 * The Magpie (§5): the thief. Flies at 6 m on a random walk between surface biomes, lingers near the
 * player, swoops on ground items every ~40 s, steals from the hand mid-dodge, drops loot after 60–120 s
 * or when hit / magnet-pulsed (metal). Immortal; a sword hit only knocks it away.
 */
export class Magpie {
  readonly root: Transform;
  readonly beak: Transform;
  readonly parts: Renderable[] = [];
  private readonly wingL: Transform;
  private readonly wingR: Transform;
  state: MagpieState = 'roam';
  private readonly target = new Vec3();
  private readonly vel = new Vec3();
  private heading = 0;
  private altitude = 6;
  private nextSteal = 40;
  private dropAt = 0;
  carrying: ItemState | null = null;
  private swoopItem: ItemState | null = null;
  private fleeT = 0;
  private readonly rng = new Rng(606);
  private retargetT = 0;
  private stealCooldown = 0;
  readonly flash = SpringF.fromFreq(0, 9, 1);
  interval = 40;
  /** Places loot must never land (keep interiors); the world fills this in. */
  noDropZones: { min: Vec3; max: Vec3 }[] = [];
  private inNoDrop(p: Vec3): boolean {
    return this.noDropZones.some((z) => p.x > z.min.x - 2 && p.x < z.max.x + 2 && p.z > z.min.z - 2 && p.z < z.max.z + 2);
  }

  constructor(private readonly ctx: Ctx, start: Vec3) {
    this.root = new Transform();
    this.root.position.copy(start); this.root.position.y += 6;
    this.root.snapPrev();
    ctx.spawn('magpie', this.root);
    const body = combine(
      sphere(0.28, 9, 6, [0.08, 0.08, 0.1]),
      translate(sphere(0.2, 8, 5, [0.95, 0.95, 0.97]), 0, -0.12, 0.05),
      translate(sphere(0.19, 8, 5, [0.08, 0.08, 0.1]), 0, 0.14, 0.32),
      translate(rotateX(cone(0.06, 0.28, 5, [0.9, 0.75, 0.3]), Math.PI / 2 - 0.2), 0, 0.1, 0.62),
      setEmissive(translate(box(0.08, 0.05, 0.7, [0.25, 0.5, 1]), 0, 0.05, -0.55), [0.2, 0.45, 1], 1.2),
    );
    const add = (name: string, mesh: ReturnType<typeof box>, parent: Transform, pos: Vec3): Transform => {
      const t = new Transform().setParent(parent);
      t.position.copy(pos);
      t.entity = ctx.spawn(name, t, mesh);
      this.parts.push(ctx.renderables.req(t.entity));
      return t;
    };
    add('magpie.body', body, this.root, new Vec3());
    const wing = (side: number) => translate(box(0.9, 0.03, 0.45, side > 0 ? [0.1, 0.1, 0.12] : [0.1, 0.1, 0.12]), side * 0.45, 0, 0);
    this.wingL = add('magpie.wingL', combine(wing(1), translate(box(0.35, 0.03, 0.3, [0.95, 0.95, 0.97]), 0.45, 0.005, -0.05)), this.root, new Vec3(0.2, 0.05, 0));
    this.wingR = add('magpie.wingR', combine(wing(-1), translate(box(0.35, 0.03, 0.3, [0.95, 0.95, 0.97]), -0.45, 0.005, -0.05)), this.root, new Vec3(-0.2, 0.05, 0));
    this.beak = new Transform().setParent(this.root);
    this.beak.position.set(0, -0.2, 0.55);
    this.beak.entity = ctx.spawn('magpie.beak', this.beak);
    this.pickRoamTarget(null);
  }

  get position(): Vec3 { return this.root.position; }

  private pickRoamTarget(player: Player | null): void {
    const pb = player ? biomeAt(player.position.x, player.position.z) : null;
    let biome: BiomeId;
    if (player && pb && SURFACE.includes(pb) && this.rng.chance(0.65)) {
      // linger near the player
      const a = this.rng.range(0, Math.PI * 2), r = this.rng.range(8, 30);
      this.target.set(player.position.x + Math.cos(a) * r, 0, player.position.z + Math.sin(a) * r);
      biome = pb;
    } else {
      biome = this.rng.pick(SURFACE);
      const b = BIOMES[biome];
      const a = this.rng.range(0, Math.PI * 2), r = this.rng.range(5, b.radius - 10);
      this.target.set(b.center.x + Math.cos(a) * r, 0, b.center.z + Math.sin(a) * r);
    }
    this.retargetT = this.rng.range(6, 14);
  }

  /** Called by the sword resolver: knocks the bird back and makes it drop what it holds. */
  hit(from: Vec3, items: Items, particles: Particles): void {
    this.flash.value = 1;
    const dir = _v.copy(this.position).sub(from); dir.y = 0.4; dir.normalize();
    this.vel.addScaled(dir, 9);
    this.dropLoot(items);
    this.state = 'flee'; this.fleeT = 8;
    particles.burst(this.position, 8, [0.1, 0.1, 0.12], 4, 0.12, 0.6, 0);
    particles.burst(this.position, 4, [0.95, 0.95, 0.97], 3, 0.1, 0.6, 0);
    this.ctx.sfx.magpie(this.position);
    this.ctx.sfx.hit(this.position);
  }

  dropLoot(items: Items): void {
    const it = this.carrying;
    if (!it) return;
    if (this.inNoDrop(this.position) && this.state === 'carry') { this.dropAt = this.ctx.time + 3; return; } // keep flying, drop later
    this.carrying = null;
    const p = this.beak.getWorldPosition(_v);
    if (this.inNoDrop(p)) { p.x += 14; }
    items.release(it, p.clone(), _d.set(this.vel.x * 0.3, 0.5, this.vel.z * 0.3));
    this.ctx.sfx.magpie(this.position);
  }

  /** Public: put an item straight into the beak (Mode B start, save restore). */
  grabNow(it: ItemState, items: Items): void { this.grab(it, items); }

  private grab(it: ItemState, items: Items): void {
    if (it.loc === 'held') items.release(it, items.worldPos(it, _v).clone());
    if (it.loc !== 'ground') return;
    it.loc = 'magpie';
    it.sleeping = true;
    it.t.setParent(this.beak);
    it.t.position.set(0, -0.15, 0);
    it.t.rotation.copy(it.def.handRot);
    it.t.snapPrev();
    this.carrying = it;
    this.state = 'carry';
    this.dropAt = this.ctx.time + this.rng.range(60, 120);
    this.nextSteal = this.interval * this.rng.range(0.8, 1.2);
    this.ctx.sfx.swoop(this.position);
    this.pickRoamTarget(null);
  }

  update(dt: number, player: Player, items: Items, particles: Particles): void {
    const st = this.ctx.statics;
    const p = this.position;
    const time = this.ctx.time;
    this.flash.target = 0;
    const f = this.flash.update(dt);
    for (const r of this.parts) r.flash = f;
    this.nextSteal -= dt;
    this.stealCooldown = Math.max(0, this.stealCooldown - dt);
    const playerOnSurface = SURFACE.includes(biomeAt(player.position.x, player.position.z));
    const chaliceHeld = items.isHolding('chalice');

    // steal from the hand mid-dodge (§5)
    if (this.state !== 'flee' && !this.carrying && items.held && player.state === 'dodge' && player.invulnerable && this.stealCooldown <= 0 && playerOnSurface) {
      if (p.distanceToXZ(player.position) < 5 && p.y - player.position.y < 4.5) {
        const it = items.held;
        items.drop(0.2);
        this.grab(it, items);
        this.stealCooldown = 25;
        particles.puff(player.position, 5);
      }
    }

    switch (this.state) {
      case 'roam': {
        this.retargetT -= dt;
        if (chaliceHeld && playerOnSurface) {
          // drawn to the chalice: circle the player closely
          const a = time * 0.6;
          this.target.set(player.position.x + Math.cos(a) * 7, 0, player.position.z + Math.sin(a) * 7);
        } else if (this.retargetT <= 0 || p.distanceToXZ(this.target) < 4) this.pickRoamTarget(player);
        // look for loot
        if (this.nextSteal <= 0) {
          let best: ItemState | null = null, bd = 25;
          for (const it of items.all) {
            if (it.loc !== 'ground' || it.def.heavy || !it.touched) continue;
            if (!SURFACE.includes(biomeAt(it.t.position.x, it.t.position.z))) continue;
            const d = it.t.position.distanceToXZ(p);
            if (d < bd) { best = it; bd = d; }
          }
          if (best) { this.swoopItem = best; this.state = 'swoop'; this.ctx.sfx.swoop(this.position); }
          else this.nextSteal = 8;
        }
        this.altitude = 6;
        break;
      }
      case 'swoop': {
        const it = this.swoopItem;
        if (!it || it.loc !== 'ground') { this.state = 'roam'; this.nextSteal = 10; break; }
        this.target.copy(it.t.position);
        const d = p.distanceToXZ(it.t.position);
        this.altitude = clamp(d * 0.6, 0.6, 6);
        if (d < 1.2 && p.y - it.t.position.y < 1.6) { this.grab(it, items); this.swoopItem = null; }
        break;
      }
      case 'carry': {
        this.retargetT -= dt;
        if (this.retargetT <= 0 || p.distanceToXZ(this.target) < 4) this.pickRoamTarget(null);
        this.altitude = 6;
        if (time >= this.dropAt || !this.carrying) { this.dropLoot(items); this.state = 'roam'; }
        // the sword or magnet can also make it drop (handled externally via hit()/dropLoot())
        break;
      }
      case 'flee': {
        this.fleeT -= dt;
        const away = _d.copy(p).sub(player.position); away.y = 0; away.normalize();
        this.target.copy(p).addScaled(away, 30);
        this.altitude = 9;
        if (this.fleeT <= 0) { this.state = 'roam'; this.pickRoamTarget(player); }
        break;
      }
    }

    // steering: turn toward target, fly at a steady speed, hold altitude above terrain
    const to = _v.copy(this.target).sub(p); to.y = 0;
    const want = Math.atan2(to.x, to.z);
    this.heading += clamp(wrapAngle(want - this.heading), -2.5 * dt, 2.5 * dt);
    const speed = this.state === 'swoop' ? 9 : this.state === 'flee' ? 11 : 6;
    this.vel.x = damp(this.vel.x, Math.sin(this.heading) * speed, 0.3, dt);
    this.vel.z = damp(this.vel.z, Math.cos(this.heading) * speed, 0.3, dt);
    p.x += this.vel.x * dt; p.z += this.vel.z * dt;
    const groundY = st.ground(p.x, 60, p.z, 100).y;
    const targetY = (this.state === 'swoop' && this.swoopItem ? this.swoopItem.t.position.y : groundY) + this.altitude;
    p.y = damp(p.y, Math.max(targetY, groundY + 0.6), 0.25, dt);
    this.root.rotation.setEuler(this.heading, clamp((targetY - p.y) * 0.15, -0.4, 0.4), -clamp(wrapAngle(want - this.heading), -0.6, 0.6));
    // wings flap
    const flap = Math.sin(time * 14) * 0.5 + (this.state === 'swoop' ? 0.1 : 0);
    this.wingL.rotation.setAxisAngle(_z, flap);
    this.wingR.rotation.setAxisAngle(_z, -flap);
  }

  serialize(): { pos: [number, number, number]; carrying: string | null } {
    return { pos: [this.position.x, this.position.y, this.position.z], carrying: this.carrying?.def.id ?? null };
  }
}
const _v = new Vec3(), _d = new Vec3(), _z = new Vec3(0, 0, 1);
